import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

// Import local size chart images
import SIZE_CHART_M_URL from "../../assets/image/NEW-MONSINI-SIZE-CHART.png";
import SIZE_CHART_B_URL from "../../assets/image/Enchanted-BRIDAL-SIZE-CHART-scaled.jpg";
import SIZE_CHART_E_URL from "../../assets/image/Enchanted-MOB-SIZE-CHART-scaled.jpg";
import Menu from "./Menu";

// The base URL for the server to construct the full image path
const SERVER_BASE_URL = "https://admin.monsinidress.com";

// --- INLINE SVG ICONS ---

// 1. Close (X-Mark)
const CloseIcon = ({ className = "w-7 h-7", color = "white" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill={color === "white" ? "white" : "currentColor"}
		className={className}>
		<path
			fillRule="evenodd"
			d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
			clipRule="evenodd"
		/>
	</svg>
);

// 2. Chevron Down (for Dropdown)
const ChevronDownIcon = ({ className = "ml-1 h-5 w-5" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 20 20"
		fill="currentColor"
		className={className}>
		<path
			fillRule="evenodd"
			d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
			clipRule="evenodd"
		/>
	</svg>
);

// 3a. Magnify Plus (Zoom In)
const MagnifyPlusIcon = ({ className = "w-7 h-7", color = "white" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="none"
		stroke={color}
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}>
		<circle cx="11" cy="11" r="8"></circle>
		<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
		<line x1="11" y1="8" x2="11" y2="14"></line>
		<line x1="8" y1="11" x2="14" y2="11"></line>
	</svg>
);

// 3b. Magnify Minus (Zoom Out)
const MagnifyMinusIcon = ({ className = "w-7 h-7", color = "white" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="none"
		stroke={color}
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}>
		<circle cx="11" cy="11" r="8"></circle>
		<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
		<line x1="8" y1="11" x2="14" y2="11"></line>
	</svg>
);

// 4. Menu Icon
const MenuAltIcon = ({
	className = "w-[1.8rem] h-[1.8rem] text-gray-800 hover:text-pink-600",
}) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}>
		<path d="M4 6h16M4 12h16M4 18h16"></path>
	</svg>
);

// 5. Search Icon
const SearchIcon = ({ className = "w-6 h-6 text-black" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}>
		<circle cx="11" cy="11" r="8"></circle>
		<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
	</svg>
);

// --- DROPDOWN MENU COMPONENT ---
const DropdownMenu = ({ title, options, sizeChartImageUrl }) => {
	console.log("sizeChartImageUrl", sizeChartImageUrl);

	const navigate = useNavigate();
	const [showSizeChart, setShowSizeChart] = useState(false);
	const [zoomed, setZoomed] = useState(false);

	// This function constructs the full image URL
	function getFullImageUrl(imagePath) {
		if (!imagePath) return "";

		// Ensure path starts correctly and combine with base URL
		const cleanPath = imagePath.replace(/^uploads\//i, "uploads/");
		return `${SERVER_BASE_URL}/${cleanPath}`;
	}

	// Function to get the appropriate size chart image (API or correct local fallback)
	const getSizeChartImage = (category, imageUrl) => {
		console.log("Getting size chart for:", category);
		console.log("API image URL:", imageUrl);

		// If API provided a valid size chart image, use it
		if (imageUrl && imageUrl !== null) {
			console.log("Using API size chart:", imageUrl);
			// Check if it's a local path (needs server URL) or already a full URL
			if (imageUrl.startsWith("uploads/")) {
				return getFullImageUrl(imageUrl);
			}
			return imageUrl;
		}

		// If API returned null or no image, use local/dummy images based on category
		console.log("Using local/dummy image for:", category);
		switch (category) {
			case "Prom":
				return SIZE_CHART_M_URL;
			case "Enchanted Bridal":
				return SIZE_CHART_B_URL;
			case "Enchanted Evening":
				return SIZE_CHART_E_URL;
			default:
				return SIZE_CHART_M_URL; // Always return a fallback image
		}
	};

	// Helper function to determine the ultimate fallback URL for onError
	const getUltimateFallbackUrl = (category) => {
		console.log("Getting ultimate fallback for:", category);
		switch (category) {
			case "Prom":
				return SIZE_CHART_M_URL;
			case "Enchanted Bridal":
				return SIZE_CHART_B_URL;
			case "Enchanted Evening":
				return SIZE_CHART_E_URL;
			default:
				return SIZE_CHART_M_URL;
		}
	};

	// Modal close handler using escape key
	useEffect(() => {
		const handleEscape = (e) => {
			if (e.key === "Escape" && showSizeChart) {
				setShowSizeChart(false);
			}
		};
		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [showSizeChart]);

	function handleNavigate(optionValue) {
		if (optionValue === "sizeChart") {
			// Always show size chart - we now have fallback images for all categories
			setShowSizeChart(true);
		} else {
			navigate(optionValue);
		}
	}

	return (
		<li className="relative group font-lora text-black font-medium xl:text-[18px] text-[15px]">
			{/* 🪟 Size Chart Modal */}
			{showSizeChart && (
				<div
					className="fixed inset-0 h-screen bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
					onClick={() => setShowSizeChart(false)} // Close when clicking backdrop
				>
					<div
						className="relative h-full w-full rounded-lg p-0 sm:p-6 max-w-7xl flex flex-col justify-center items-center shadow-xl"
						onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
					>
						{/* ❌ Close Button - Now uses SVG component */}
						<button
							onClick={() => setShowSizeChart(false)}
							className="absolute top-4 right-4 z-10 p-2 bg-white/20 rounded-full hover:bg-white/40 transition-colors duration-200"
							aria-label="Close">
							<CloseIcon />
						</button>

						{/* 🔍 Zoom Button - Now uses SVG components */}
						<button
							onClick={() => setZoomed((prev) => !prev)}
							className="absolute top-4 left-4 z-10 p-2 bg-white/20 rounded-full hover:bg-white/40 transition-colors duration-200"
							aria-label="Zoom">
							{zoomed ? <MagnifyMinusIcon /> : <MagnifyPlusIcon />}
						</button>

						{/* 🖼️ Image Container */}
						<div
							className={`overflow-auto w-full h-full flex items-center justify-center p-8 transition-all duration-500 ease-in-out`}>
							<img
								src={getSizeChartImage(title, sizeChartImageUrl)}
								alt={`${title} Size Chart`}
								className={`h-auto rounded-md object-contain transition-transform duration-500 ease-in-out ${
									zoomed ? "scale-150" : "scale-100 max-h-full max-w-full"
								}`}
								onError={(e) => {
									console.error(
										`Failed to load image for ${title}. Attempting ultimate fallback.`
									);
									// FIXED: Use the new helper function to set the correct category-specific ultimate fallback
									e.target.src = getUltimateFallbackUrl(title);
								}}
							/>
						</div>
					</div>
				</div>
			)}

			{/* Dropdown Trigger */}
			<span className="flex items-center cursor-pointer py-2 xl:px-3 px-1 text-gray-700 hover:text-pink-600 transition-colors duration-200">
				{title}
				<ChevronDownIcon className="ml-1 h-5 w-5 transition-transform group-hover:rotate-180" />
			</span>

			{/* Dropdown Content */}
			<div className="absolute left-1/2 -translate-x-1/2 mt-0 w-44 rounded-md lg:shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-40 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-in-out">
				<div className="py-1">
					{options.length === 0 && (
						<span className="block w-full px-4 py-2 text-sm text-gray-500">
							No collections available.
						</span>
					)}
					{options.map((option, index) => (
						<button
							key={index}
							onClick={() => handleNavigate(option.value)}
							className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-pink-600 text-center transition-colors duration-200">
							{option.label}
						</button>
					))}
				</div>
			</div>
		</li>
	);
};

// --- DUMMY DATA ---
const dummyData = {
	Prom: {
		years: [],
		sizeChartImage: SIZE_CHART_M_URL, // Use local image for dummy data
	},
	Evening: {
		years: [],
		sizeChartImage: SIZE_CHART_E_URL, // Use local image for dummy data
	},
	Bridal: {
		years: [],
		sizeChartImage: SIZE_CHART_B_URL, // Use local image for dummy data
	},
};

// --- MAIN NAVIGATION COMPONENT ---
export default function NavLg({ setMenu, menu, isScrollingUp, setShowSearch }) {
	const [data, setData] = useState(null);
	const navigate = useNavigate();

	// 1. Define the desired display order (using the formatTitle output)
	const DISPLAY_ORDER = ["Prom", "Enchanted Evening", "Enchanted Bridal"];
	// Helper map to quickly find the index of a category title
	const ORDER_MAP = DISPLAY_ORDER.reduce((acc, title, index) => {
		acc[title] = index;
		return acc;
	}, {});

	useEffect(() => {
		const fetchProducts = async () => {
			try {
				// Using the brand "Monsini" as specified in the original request
				const response = await fetch(`${SERVER_BASE_URL}/api/navbar`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ brand: "Monsini" }),
				});

				if (!response.ok) {
					throw new Error("Failed to fetch products");
				}
				const productsData = await response.json();

				// Set data state to the 'data' key from the API response
				if (productsData.data && Object.keys(productsData.data).length > 0) {
					const apiData = productsData.data || {};

					// If API returns less than 3 categories, supplement with dummy data
					const apiCategories = Object.keys(apiData);
					if (apiCategories.length < 3) {
						console.log(
							`API returned ${apiCategories.length} categories, supplementing with dummy data`
						);

						// Create a merged data object
						const mergedData = { ...apiData };
						const dummyCategories = Object.keys(dummyData);

						// Add dummy categories until we have at least 3
						for (const category of dummyCategories) {
							if (Object.keys(mergedData).length >= 3) break;
							if (!mergedData[category]) {
								mergedData[category] = dummyData[category];
							}
						}

						setData(mergedData);
					} else {
						setData(apiData);
					}
				} else {
					// If API returns empty data, use dummy data as fallback
					console.log("API returned empty data, using dummy data as fallback");
					setData(dummyData);
				}
			} catch (error) {
				console.error("Error fetching products:", error);
				// Use dummy data as fallback on error
				console.log("Using dummy data due to API error");
				setData(dummyData);
			}
		};

		fetchProducts();
	}, []);

	// Helper function to format the title for display
	const formatTitle = (category) => {
		if (category === "Prom") return "Prom";
		if (category === "Evening") return "Enchanted Evening";
		if (category === "Bridal") return "Enchanted Bridal";
		return category;
	};

	// Helper function to generate dropdown options from years and add the size chart option
	const generateOptions = (category, categoryData) => {
		const years = categoryData?.years || [];
		const options = years.map((year) => ({
			label: `${year} Collection`,
			value: `/${category.toLowerCase()}/${year}`,
		}));

		// If no years are fetched, provide a default link to view all items in that category.
		if (options.length === 0 && !categoryData?.sizeChartImage) {
			options.push({
				label: `View All ${formatTitle(category)}`,
				value: `/${category.toLowerCase()}/all`,
			});
		}

		// Logic to always add the 'Size Chart' option (as per prior request)
		options.push({ label: "Size Chart", value: "sizeChart" });

		return options;
	};

	// Filter categories to only show those with years or a size chart image
	let validCategories = data
		? Object.entries(data).filter(
				([, categoryData]) =>
					(categoryData.years && categoryData.years.length > 0) ||
					categoryData.sizeChartImage
		  )
		: [];

	// 2. Sort the categories based on the defined DISPLAY_ORDER
	validCategories.sort(([categoryA], [categoryB]) => {
		const titleA = formatTitle(categoryA);
		const titleB = formatTitle(categoryB);

		const indexA =
			ORDER_MAP[titleA] !== undefined ? ORDER_MAP[titleA] : Infinity;
		const indexB =
			ORDER_MAP[titleB] !== undefined ? ORDER_MAP[titleB] : Infinity;

		return indexA - indexB;
	});

	const hasDropdownData = validCategories.length > 0;

	return (
		<nav
			className={`bg-white shadow-md z-30 fixed top-0 w-full flex justify-between items-center px-4 md:py-7 lg:py-5 py-[1.7rem] lg:px-7 xl:px-12 md:px-8 transition-transform duration-300 ease-in-out border-b border-gray-100 ${
				isScrollingUp ? "translate-y-0" : "-translate-y-full"
			}`}>
			{/* Mobile Menu Button */}
			<button
				className="lg:hidden transition-all duration-200 ease-in-out transform"
				onClick={() => setMenu(!menu)}
				aria-label="Open mobile menu">
				<MenuAltIcon />
			</button>

			{/* Logo */}
			<div
				className="relative w-fit h-fit cursor-pointer"
				onClick={() => navigate("/")}>
				<h2 className="myclass text-5xl font-medium tracking-tight text-gray-800 leading-tight">
					Monsini
					<span
						className="absolute font-poppins leading-3 tracking-[0.01px]
  bottom-0 right-0 font-bold text-[10px] text-[#ff2e38]">
						PROUDLY CANADIAN
					</span>
				</h2>
			</div>

			{/* Navbar Links */}
			<ul className="lg:flex font-lora items-center gap-1 xl:p-2 p-1 hidden grow justify-center text-gray-700 text-base py-4">
				<li className="xl:p-2 p-1 font-medium xl:text-[18px] text-[15px]">
					<Link
						to="/"
						className="hover:text-pink-600 transition-colors duration-200 font-medium">
						Home
					</Link>
				</li>

				{/* Dynamic Dropdowns - Now rendered in sorted order */}
				{hasDropdownData &&
					validCategories.map(([category, categoryData]) => (
						<DropdownMenu
							key={category}
							title={formatTitle(category)}
							options={generateOptions(category, categoryData)}
							sizeChartImageUrl={categoryData.sizeChartImage}
						/>
					))}

				{/* Standard Links */}
				<li className="xl:p-2 p-1 font-medium xl:text-[18px] text-[15px]">
					<Link
						to="/about"
						className="hover:text-pink-600 transition-colors duration-200 font-medium">
						About Us
					</Link>
				</li>
				<li className="xl:p-2 p-1 font-medium xl:text-[18px] text-[15px]">
					<Link
						to="/contact"
						className="hover:text-pink-600 transition-colors duration-200 font-medium">
						Contact Us
					</Link>
				</li>
				<li className="xl:p-2 p-1">
					<Link
						to="https://market.monsiniprom.com/"
						className="text-pink-600 hover:text-pink-800 transition-colors duration-200 font-bold border border-pink-600 px-4 py-1.5 rounded-md">
						Retailer Login
					</Link>
				</li>
			</ul>

			{/* Search Button */}
			<div className="flex items-center justify-center gap-4">
				<button onClick={() => setShowSearch(true)}>
					<SearchIcon />
				</button>
			</div>

			{/* Mobile Menu */}
			<Menu
				setMenu={setMenu}
				menu={menu}
				data={validCategories} // Passed the sorted data to the mobile menu
				formatTitle={formatTitle}
				generateOptions={generateOptions}
			/>
		</nav>
	);
}
